/**
 * Size Finder — standalone PAGE version
 * assets/size-finder-page.js
 *
 * Same recommendation logic as fit-finder.js, but rendered
 * directly inside a page (no modal/overlay) and with no
 * "apply to product variant" step — it just shows the result
 * with a link to shop that size.
 */

(function () {
  'use strict';

  /* Klaviyo — fill both in before this will work.
     Public key: Klaviyo → Settings → API keys → Public API key (6 characters)
     List ID:    Klaviyo → Lists & Segments → your list → Settings (6 characters) */
  var KLAVIYO_PUBLIC_KEY = 'TxLhdM';
  var KLAVIYO_LIST_ID    = 'Xm65aL';

  /* ─────────────────────────────────────────────
     SIZES + BRAND SIZE CHART — same data as fit-finder.js
  ───────────────────────────────────────────── */
  var SIZES = ['8Y', '9Y', '10Y', '11-12Y', '13-14Y'];

  var SIZE_CHART = {
    '8Y':     { height: 131, heightRange: [120, 136], chest: 68, waist: 58, hip: 72 },
    '9Y':     { height: 141, heightRange: [136, 145], chest: 70, waist: 60, hip: 74 },
    '10Y':    { height: 148, heightRange: [145, 152], chest: 72, waist: 62, hip: 76 },
    '11-12Y': { height: 156, heightRange: [152, 160], chest: 76, waist: 64, hip: 81 },
    '13-14Y': { height: 164, heightRange: [160, 175], chest: 80, waist: 68, hip: 86 },
  };

  /* ─────────────────────────────────────────────
     RECOMMENDATION ENGINE (unchanged from fit-finder.js)
  ───────────────────────────────────────────── */
  function scoreSize(sizeKey, heightCm, weightKg) {
    var chart = SIZE_CHART[sizeKey];
    if (!chart) {
      var idx = SIZES.indexOf(sizeKey);
      var mid = (SIZES.length - 1) / 2;
      return 50 - Math.abs(idx - mid) * 10;
    }

    var score = 0;
    var hMin = chart.heightRange[0];
    var hMax = chart.heightRange[1];
    if (heightCm >= hMin && heightCm <= hMax) {
      score += 70;
    } else {
      var hDiff = heightCm < hMin ? hMin - heightCm : heightCm - hMax;
      score += Math.max(0, 70 - hDiff * 3);
    }

    if (weightKg && weightKg > 0) {
      var expectedWeight = (chart.height - 100) * 0.9;
      var wDiff = Math.abs(weightKg - expectedWeight);
      score += Math.max(0, 30 - wDiff * 1.5);
    } else {
      score += 15;
    }

    return score;
  }

  function recommendSize(height, weight, build, fit) {
    var scores = SIZES.map(function (size) {
      return { size: size, score: scoreSize(size, height, weight) };
    });
    scores.sort(function (a, b) { return b.score - a.score; });

    var bestIdx   = SIZES.indexOf(scores[0].size);
    var secondIdx = SIZES.indexOf(scores[1] ? scores[1].size : scores[0].size);

    var scoreDiff  = scores[0].score - (scores[1] ? scores[1].score : 0);
    var borderline = scoreDiff < 15;

    if (build === 'slim' && borderline && secondIdx < bestIdx) bestIdx = secondIdx;
    if (build === 'broad') bestIdx = Math.min(SIZES.length - 1, bestIdx + 1);
    if (fit === 'room' || fit === 'relaxed') bestIdx = Math.min(SIZES.length - 1, bestIdx + 1);

    return SIZES[bestIdx];
  }

  function getNextSize(size) {
    var i = SIZES.indexOf(size);
    return i >= 0 && i < SIZES.length - 1 ? SIZES[i + 1] : null;
  }

  function getConfidence(heightCm, size, build, fit) {
    var chart = SIZE_CHART[size];
    if (!chart) return 90;
    var hMin = chart.heightRange[0];
    var hMax = chart.heightRange[1];
    if (heightCm >= hMin && heightCm <= hMax) {
      if (build === 'regular' && fit === 'perfect') return 99;
      if (fit === 'perfect') return 97;
      return 95;
    }
    var diff = heightCm < hMin ? hMin - heightCm : heightCm - hMax;
    if (diff <= 3) return 93;
    if (diff <= 6) return 90;
    return 87;
  }

  function parseSizeLabel(size) {
    var s = String(size).trim();
    var rangeMatch = s.match(/^(\d+)\s*[-–]\s*(\d+)/);
    if (rangeMatch) return 'Ages ' + rangeMatch[1] + '–' + rangeMatch[2];
    var singleMatch = s.match(/^(\d+)/);
    if (singleMatch) return 'Age ' + singleMatch[1];
    return s;
  }

  /* ─────────────────────────────────────────────
     QUIZ STATE
  ───────────────────────────────────────────── */
  var state = { step: 0, age: null, height: 141, weight: 32, build: null, fit: null };

  function dots(current) {
    var html = '<div class="ff-dots">';
    for (var i = 0; i < 5; i++) {
      html += '<div class="ff-dot' + (i <= current ? ' active' : '') +
              '" style="width:' + (i <= current ? '20' : '8') + 'px"></div>';
    }
    return html + '</div>';
  }

  function renderIntro() {
    return (
      '<div class="ff-intro-hero">' +
        '<div style="font-size:34px">✨</div>' +
        '<h2>Find Her Perfect Fit</h2>' +
        '<p>Answer 4 quick questions about your daughter. No tape measure, no guessing — just the right size, first time.</p>' +
      '</div>' +
      '<ul class="ff-feature-list">' +
        '<li><div class="ff-feature-icon">📏</div><span><strong>No tape measure needed</strong> — we only ask what you already know</span></li>' +
        '<li><div class="ff-feature-icon">⚡</div><span><strong>Takes 30 seconds</strong> — four simple questions</span></li>' +
        '<li><div class="ff-feature-icon">🎯</div><span><strong>Based on your actual size chart</strong> — not a generic guess</span></li>' +
        '<li><div class="ff-feature-icon">🌱</div><span><strong>Growth-aware</strong> — we\'ll suggest when to size up</span></li>' +
      '</ul>' +
      '<button class="ff-btn-primary" data-action="go" data-step="1">Let\'s Find Her Size →</button>'
    );
  }

  function renderAge() {
    var grid = '<div class="ff-size-grid">';
    SIZES.forEach(function (size) {
      var label = parseSizeLabel(size);
      var chart = SIZE_CHART[size];
      grid +=
        '<div class="ff-size-card' + (state.age === size ? ' selected' : '') +
        '" data-action="pick" data-key="age" data-val="' + size + '">' +
          '<div class="ff-size-card__badge">' + size + '</div>' +
          '<div class="ff-size-card__label">' + label + '</div>' +
          '<div class="ff-size-card__hint">' + (chart ? chart.height + ' cm' : '') + '</div>' +
        '</div>';
    });
    grid += '</div>';

    return dots(0) +
      '<h2 class="ff-title">What size is she currently wearing? 👕</h2>' +
      '<p class="ff-sub">Pick the closest size — we\'ll fine-tune from there</p>' +
      grid +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="0">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="2"' + (!state.age ? ' disabled' : '') + '>Next →</button>' +
      '</div>';
  }

  function renderHeight() {
    return dots(1) +
      '<h2 class="ff-title">How tall is she? 📐</h2>' +
      '<p class="ff-sub">Approximate is perfectly fine — just your best guess!</p>' +
      '<div class="ff-slider-row">' +
        '<div class="ff-slider-header">' +
          '<span class="ff-slider-label">Height</span>' +
          '<span class="ff-slider-val" id="ff-height-val">' + state.height + ' cm</span>' +
        '</div>' +
        '<input type="range" class="ff-range" id="ff-height" min="115" max="175" step="1" value="' + state.height + '">' +
        '<div class="ff-slider-ticks"><span>115 cm</span><span>175 cm</span></div>' +
      '</div>' +
      '<div class="ff-slider-row">' +
        '<div class="ff-slider-header">' +
          '<span class="ff-slider-label">Weight <small>(optional)</small></span>' +
          '<span class="ff-slider-val" id="ff-weight-val">' + state.weight + ' kg</span>' +
        '</div>' +
        '<input type="range" class="ff-range" id="ff-weight" min="20" max="75" step="1" value="' + state.weight + '">' +
        '<div class="ff-slider-ticks"><span>20 kg</span><span>75 kg</span></div>' +
      '</div>' +
      '<div class="ff-tip">💡 <strong>Tip:</strong> If you measured her recently, add 1–2 cm for a little growing room.</div>' +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="1">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="3">Next →</button>' +
      '</div>';
  }

  function renderBuild() {
    var opts = [
      { v: 'slim',    l: 'Slim',            d: 'Lean and narrow — clothes often feel a little loose', e: '🧍' },
      { v: 'regular', l: 'Regular',          d: 'Average proportions — most standard sizes fit well', e: '🧑' },
      { v: 'broad',   l: 'Broad / Athletic', d: 'Stronger or wider — sometimes needs to size up', e: '🏋️' },
    ];
    var cards = opts.map(function (o) {
      return '<div class="ff-option-card' + (state.build === o.v ? ' selected' : '') +
             '" data-action="pick" data-key="build" data-val="' + o.v + '">' +
        '<span class="ff-card-emoji">' + o.e + '</span>' +
        '<div><div class="ff-card-label">' + o.l + '</div><div class="ff-card-sub">' + o.d + '</div></div>' +
      '</div>';
    }).join('');
    return dots(2) +
      '<h2 class="ff-title">What\'s her build? 🧍</h2>' +
      '<p class="ff-sub">Which best describes her body shape?</p>' +
      cards +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="2">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="4"' + (!state.build ? ' disabled' : '') + '>Next →</button>' +
      '</div>';
  }

  function renderFit() {
    var opts = [
      { v: 'perfect', l: 'Perfect fit right now', d: 'Fits her well today — great for sport, dance, or special occasions', e: '🎯' },
      { v: 'room',    l: 'Room to grow',           d: 'A little extra room — ideal for everyday wear and fast-growing girls', e: '🌱' },
      { v: 'relaxed', l: 'Relaxed & comfortable',  d: 'Loose and easy — perfect for lounging, play, and all-day wear', e: '😌' },
    ];
    var cards = opts.map(function (o) {
      return '<div class="ff-option-card' + (state.fit === o.v ? ' selected' : '') +
             '" data-action="pick" data-key="fit" data-val="' + o.v + '">' +
        '<span class="ff-card-emoji">' + o.e + '</span>' +
        '<div><div class="ff-card-label">' + o.l + '</div><div class="ff-card-sub">' + o.d + '</div></div>' +
      '</div>';
    }).join('');
    return dots(3) +
      '<h2 class="ff-title">How should it fit? 👗</h2>' +
      '<p class="ff-sub">What matters most to you and your daughter?</p>' +
      cards +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="3">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="5"' + (!state.fit ? ' disabled' : '') + '>See Her Size →</button>' +
      '</div>';
  }

  function renderResult() {
    var rec  = recommendSize(state.height, state.weight, state.build, state.fit);
    var next = getNextSize(rec);
    var cf   = getConfidence(state.height, rec, state.build, state.fit);

    var buildLabels = { slim: 'a slim build', regular: 'a regular build', broad: 'a broad / athletic build' };
    var fitLabels   = { perfect: 'a perfect fit right now', room: 'room to grow', relaxed: 'a relaxed comfortable fit' };

    var chart = SIZE_CHART[rec];
    var measureNote = chart
      ? '<div class="ff-measure-note">' +
          '<div class="ff-measure-row"><span>Typical height</span><strong>' + chart.height + ' cm</strong></div>' +
          '<div class="ff-measure-row"><span>Chest</span><strong>' + chart.chest + ' cm</strong></div>' +
          '<div class="ff-measure-row"><span>Waist</span><strong>' + chart.waist + ' cm</strong></div>' +
          '<div class="ff-measure-row"><span>Hip</span><strong>' + chart.hip + ' cm</strong></div>' +
        '</div>'
      : '';

    var shopUrl = '/collections/all?filter.v.option.size=' + encodeURIComponent(rec);

    var nextSizeNote = next
      ? '<p class="ff-sub" style="margin-top:8px;">🌱 Also fits well in <strong>Size ' + next + '</strong> if you\'d like extra room to grow.</p>'
      : '';

    return dots(4) +
      '<h2 class="ff-title">Her Perfect Fit! 🎉</h2>' +
      '<p class="ff-sub">Based on your answers and our size chart</p>' +
      '<div class="ff-result-hero">' +
        '<div class="ff-result-size">' + rec + '</div>' +
        '<div class="ff-result-label">Recommended Size</div>' +
        '<div class="ff-result-badge"><span class="check">✓</span> ' + cf + '% confidence — Great fit for her!</div>' +
      '</div>' +
      '<div class="ff-result-summary">' +
        'Based on her <strong>height of ' + state.height + ' cm</strong>, <strong>' + buildLabels[state.build] + '</strong>, and your preference for <strong>' + fitLabels[state.fit] + '</strong>, we recommend <strong>Size ' + rec + '</strong>.' +
      '</div>' +
      measureNote +
      nextSizeNote +
      '<div class="ff-email-capture" id="ff-email-capture" style="margin-top:20px;padding:20px;background:#faf7f8;border:1px solid #e8dfe3;border-radius:12px;">' +
        '<div style="font-weight:600;margin-bottom:6px;">Want her size saved?</div>' +
        '<p style="margin:0 0 14px;color:#666;font-size:14px;line-height:1.5;">' +
          'We\'ll email this to you, and check in around ten months from now, when she\'s likely ready for the next size.' +
        '</p>' +
        '<div style="display:flex;gap:8px;flex-wrap:wrap;">' +
          '<input type="email" id="ff-email" placeholder="Your email" autocomplete="email" ' +
            'style="flex:1 1 180px;min-width:0;padding:11px 13px;border:1px solid #ccc;border-radius:8px;font-size:16px;">' +
          '<button class="ff-btn-primary" data-action="subscribe" style="flex:0 0 auto;">Send it to me</button>' +
        '</div>' +
        '<div id="ff-email-msg" style="margin-top:10px;font-size:14px;" role="status" aria-live="polite"></div>' +
      '</div>' +
      '<a class="ff-btn-primary" style="display:block;text-align:center;text-decoration:none;margin-top:16px;" href="' + shopUrl + '">Shop Size ' + rec + ' →</a>' +
      '<div class="ff-restart"><button data-action="restart">Start over</button></div>';
  }
  /* ─────────────────────────────────────────────
     EMAIL CAPTURE
  ───────────────────────────────────────────── */
  function subscribe(btn) {
    var input = document.getElementById('ff-email');
    var msg   = document.getElementById('ff-email-msg');
    if (!input || !msg) return;

    var email = input.value.trim();
    if (!email || email.indexOf('@') < 1 || email.indexOf('.') < 0) {
      msg.style.color = '#a3323f';
      msg.textContent = 'That email doesn\'t look right — mind checking it?';
      input.focus();
      return;
    }

    if (!KLAVIYO_PUBLIC_KEY || !KLAVIYO_LIST_ID) {
      msg.style.color = '#a3323f';
      msg.textContent = 'Email sign-up isn\'t configured yet.';
      return;
    }

    btn.disabled = true;
    var originalLabel = btn.textContent;
    btn.textContent = 'Sending…';
    msg.textContent = '';

    var rec = recommendSize(state.height, state.weight, state.build, state.fit);

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  recommended_size: rec,
                  current_size: state.age,
                  height_cm: state.height,
                  weight_kg: state.weight,
                  build: state.build,
                  fit_preference: state.fit,
                  next_size: getNextSize(rec),
                  size_finder_completed_at: new Date().toISOString(),
                  source: 'Size Finder'
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } }
        }
      }
    };

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Klaviyo responded ' + res.status);
        var box = document.getElementById('ff-email-capture');
        if (box) {
          box.innerHTML =
            '<div style="font-weight:600;margin-bottom:6px;">Sent — check your inbox.</div>' +
            '<p style="margin:0;color:#666;font-size:14px;line-height:1.5;">' +
              'Her size is on its way. We\'ll be in touch when she\'s likely ready to size up.' +
            '</p>';
        }
      })
      .catch(function (err) {
        console.error('Size Finder subscribe failed:', err);
        btn.disabled = false;
        btn.textContent = originalLabel;
        msg.style.color = '#a3323f';
        msg.textContent = 'That didn\'t send. Try again in a moment.';
      });
  }
  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  function render() {
    var content = document.getElementById('size-finder-app');
    if (!content) return;
    var html = '';
    switch (state.step) {
      case 0: html = renderIntro();  break;
      case 1: html = renderAge();    break;
      case 2: html = renderHeight(); break;
      case 3: html = renderBuild();  break;
      case 4: html = renderFit();    break;
      case 5: html = renderResult(); break;
    }
    content.innerHTML = html;
    bindSliders();
    content.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function bindSliders() {
    var h = document.getElementById('ff-height');
    var w = document.getElementById('ff-weight');
    if (h) h.addEventListener('input', function () {
      state.height = parseInt(this.value, 10);
      var el = document.getElementById('ff-height-val');
      if (el) el.textContent = state.height + ' cm';
    });
    if (w) w.addEventListener('input', function () {
      state.weight = parseInt(this.value, 10);
      var el = document.getElementById('ff-weight-val');
      if (el) el.textContent = state.weight + ' kg';
    });
  }

  /* ─────────────────────────────────────────────
     EVENTS
  ───────────────────────────────────────────── */
  function handleClick(e) {
    var el = e.target.closest('[data-action]');
    if (!el) return;
    var action = el.dataset.action;

    if (action === 'go') {
      state.step = parseInt(el.dataset.step, 10);
      render();
    }
    if (action === 'pick') {
      state[el.dataset.key] = el.dataset.val;
      render();
    }
    if (action === 'subscribe') {
      subscribe(el);
      return;
    }
    if (action === 'restart') {
      state = { step: 0, age: null, height: 141, weight: 32, build: null, fit: null };
      render();
    }
  }

  /* ─────────────────────────────────────────────
     INIT — runs directly on page load, no modal needed
  ───────────────────────────────────────────── */
  function init() {
    var content = document.getElementById('size-finder-app');
    if (!content) return;
    content.addEventListener('click', handleClick);
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();